/**
 * Help overlay: the "?" button in the header opens a modal that explains the app in plain Czech —
 * what a swath is, why leads appear only after the human confirmation, what the pins and the live
 * dot mean. Pure DOM like banner.ts; the overlay is built lazily on first open and torn down with
 * the app. All content is static author-written HTML (no user/server data), so innerHTML is safe.
 */

const SECTIONS: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: 'Co vidím na mapě',
    body:
      'Červené plochy jsou <strong>zásahy krupobití</strong> zjištěné z meteoradaru ČHMÚ — nový ' +
      'snímek přichází zhruba každých 5 minut. Kliknutím na plochu se vpravo otevře detail zásahu.',
  },
  {
    title: 'Historie zásahů',
    body:
      'Seznam vlevo řadí zásahy podle dnů. Štítek <strong>NOVÝ</strong> označuje zásah, který jste ' +
      'v tomto prohlížeči ještě neotevřeli; po rozkliknutí zmizí.',
  },
  {
    title: 'Velikost krup',
    body:
      'Číslo v cm je <strong>odhad z radarové fyziky</strong> — berte ho orientačně. Poznámka ' +
      '„PODHODNOCENO" znamená, že bouřka byla daleko od radaru a skutečné kroupy mohly být větší.',
  },
  {
    title: 'Fialové čárkované plochy',
    body:
      'Předpověď <strong>pohybu</strong> bouřkové buňky na nejbližší desítky minut — kam se pás ' +
      'pravděpodobně posune a kdy tam dorazí (časy u servisů v detailu). Předpovídá se jen pohyb, ' +
      'ne síla bouřky — kroupy mohou cestou zeslábnout i zesílit.',
  },
  {
    title: 'Validační kandidát',
    body:
      'Modrozelená poznámka v detailu zásahu: detekce byla <strong>blízko radaru a je spolehlivě ' +
      'změřená</strong> — vhodná příležitost ověřit skutečné kroupy přímo na místě.',
  },
  {
    title: 'Potvrdit kroupy → najít servisy',
    body:
      'Oranžové tlačítko v detailu. Potvrzujete jím, že kroupy <strong>skutečně spadly</strong> — ' +
      'detekce může být planý poplach. Teprve potvrzení spustí vyhledání servisů v okolí přes ' +
      'Google; každé hledání něco stojí, proto běží až po lidské kontrole. Když zóna najde málo ' +
      'servisů (venkov), jednorázově se sama rozšíří.',
  },
  {
    title: 'Servisy a piny',
    body:
      '<strong>Modré piny</strong> jsou smluvní partneři (s kontakty). <strong>Oranžové piny</strong> ' +
      'jsou leady z Google — u nich vidíte jen kategorii a vzdálenost, jména a kontakty se z ' +
      'licenčních důvodů neukládají. Odkaz „Vizitka (Google)" otevře plnou kartu firmy ' +
      's telefonem a webem.',
  },
  {
    title: 'Zelená tečka v hlavičce',
    body:
      'Hlídka běhu: říká, jak starý je poslední zpracovaný snímek radaru. <strong>Zelená</strong> = ' +
      'živě, <strong>oranžová</strong> = zpoždění, <strong>červená</strong> = data neaktuální nebo ' +
      'server nedostupný.',
  },
  {
    title: 'Ovládání v hlavičce',
    body:
      '„Radar (odrazivost)" zapne vrstvu radarového echa nad mapou. „Synchronizovat partnery" ' +
      'načte aktuální seznam poboček z Karosář.cz.',
  },
];

/** Wire the header "?" button; returns a teardown that removes the overlay and listeners. */
export function attachHelp(button: HTMLButtonElement): () => void {
  let overlay: HTMLDivElement | null = null;

  const close = (): void => {
    overlay?.remove();
    overlay = null;
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') close();
  };

  const open = (): void => {
    if (overlay !== null) return;
    overlay = document.createElement('div');
    overlay.className = 'help-backdrop';
    overlay.innerHTML = `
      <div class="help-card" role="dialog" aria-modal="true" aria-label="Nápověda">
        <div class="help-head">
          <h2>Nápověda</h2>
          <button type="button" class="help-close" aria-label="Zavřít">×</button>
        </div>
        ${SECTIONS.map((s) => `<section><h3>${s.title}</h3><p>${s.body}</p></section>`).join('')}
      </div>`;
    // Backdrop click closes; clicks inside the card must not bubble into that.
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close();
    });
    overlay.querySelector<HTMLButtonElement>('.help-close')?.addEventListener('click', close);
    document.body.appendChild(overlay);
  };

  button.addEventListener('click', open);
  document.addEventListener('keydown', onKeydown);
  return () => {
    close();
    document.removeEventListener('keydown', onKeydown);
  };
}
